import {useEffect, useState} from 'react';
import {Check, ChevronsUpDown, Search, X} from 'lucide-react';
import {cn} from '@common-web/lib/utils';
import {trpc} from '@common-web/lib/trpc';
import {Button} from '@common-web/components/ui/button';
import {Input} from '@common-web/components/ui/input';
import {Popover, PopoverContent, PopoverTrigger} from '@common-web/components/ui/popover';
import {Badge} from '@common-web/components/ui/badge';
import {useDebounce} from '@common-web/hooks/use-debounce';
import {useConsolidatedFilters} from '@internal/pages/claims/useConsolidatedFilters';

export type MultiSelectOption = {value: string; label: string};

export function MultiSelectFilter({
  id,
  placeholder,
  options,
  selected,
  onChange,
  className,
}: {
  id?: string;
  placeholder: string;
  options: MultiSelectOption[];
  selected: string[];
  onChange: (values: string[]) => void;
  className?: string;
}) {
  const [open, setOpen] = useState(false);

  const toggle = (value: string) => {
    onChange(
      selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value],
    );
  };

  const label =
    selected.length === 0
      ? placeholder
      : selected.length === 1
        ? (options.find((o) => o.value === selected[0])?.label ?? selected[0])
        : `${selected.length} selected`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant='outline'
          role='combobox'
          aria-expanded={open}
          className={cn('justify-between font-normal', className)}
        >
          <span className='truncate'>{label}</span>
          <div className='flex items-center gap-1 ml-2 shrink-0'>
            {selected.length > 0 && (
              <Badge variant='secondary' className='h-5 px-1.5 text-xs'>
                {selected.length}
              </Badge>
            )}
            <ChevronsUpDown className='h-4 w-4 opacity-50' />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-[320px] p-0' align='start'>
        <div className='max-h-64 overflow-y-auto p-1'>
          {options.length === 0 ? (
            <p className='text-sm text-muted-foreground px-2 py-4 text-center'>No options.</p>
          ) : (
            options.map((o) => {
              const checked = selected.includes(o.value);
              return (
                <button
                  key={o.value}
                  type='button'
                  onClick={() => toggle(o.value)}
                  className='flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted text-left'
                >
                  <div
                    className={cn(
                      'h-4 w-4 shrink-0 rounded border flex items-center justify-center',
                      checked ? 'bg-primary border-primary text-primary-foreground' : 'border-input',
                    )}
                  >
                    {checked && <Check className='h-3 w-3' />}
                  </div>
                  <span className='leading-snug'>{o.label}</span>
                </button>
              );
            })
          )}
        </div>
        {selected.length > 0 && (
          <div className='border-t p-1'>
            <button
              type='button'
              onClick={() => onChange([])}
              className='w-full text-xs text-muted-foreground hover:text-foreground py-1 rounded hover:bg-muted'
            >
              Clear selection
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

/** Renders dismissible pills for all active status and ex code selections. */
export function ActiveFilterPills() {
  const {status, exCode, setStatus, setExCode} = useConsolidatedFilters();
  const {data: options} = trpc.internal.claimsTracking.consolidated.getFilterOptions.useQuery();

  const statusDescMap = new Map(options?.statuses?.map((s) => [s.status, s.description]));

  const pills = [
    ...status.map((s) => ({
      key: `status-${s}`,
      label: statusDescMap.get(s) ? `${s} — ${statusDescMap.get(s)}` : s,
      onRemove: () => setStatus(status.filter((v) => v !== s)),
    })),
    ...exCode.map((c) => ({
      key: `exCode-${c}`,
      label: `Ex code: ${c}`,
      onRemove: () => setExCode(exCode.filter((v) => v !== c)),
    })),
  ];

  if (pills.length === 0) return null;

  return (
    <div className='flex flex-wrap gap-2'>
      {pills.map((p) => (
        <ActiveChip key={p.key} label={p.label} onRemove={p.onRemove} />
      ))}
    </div>
  );
}

/** Shared group / status / search controls used across every consolidated tab. */
export function ConsolidatedFilterBar() {
  const {groupId, status, updateParams, setStatus} =
    useConsolidatedFilters();

  const {data: options} = trpc.internal.claimsTracking.consolidated.getFilterOptions.useQuery();

  const [searchInput, setSearchInput] = useState(
    () => new URLSearchParams(window.location.search).get('search') ?? '',
  );
  const debounced = useDebounce(searchInput, 400);

  const search = new URLSearchParams(window.location.search).get('search') ?? '';
  useEffect(() => {
    setSearchInput(search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    updateParams({search: debounced || null});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  const statusOptions: MultiSelectOption[] =
    options?.statuses?.map((s) => ({
      value: s.status,
      label: s.description ? `${s.status} — ${s.description}` : s.status,
    })) ?? [];

  return (
    <>
      <div className='space-y-1.5 flex-1 min-w-[200px]'>
        <label className='block text-xs font-medium text-muted-foreground' htmlFor='claims-search'>
          Search
        </label>
        <div className='relative'>
          <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
          <Input
            id='claims-search'
            className={cn('pl-9', searchInput && 'pr-9')}
            placeholder='Claim ID, group, subscriber, batch…'
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          {searchInput ? (
            <Button
              type='button'
              variant='ghost'
              size='icon'
              className='absolute right-0.5 top-1/2 -translate-y-1/2 h-8 w-8'
              onClick={() => setSearchInput('')}
              aria-label='Clear search'
            >
              <X className='h-4 w-4' />
            </Button>
          ) : null}
        </div>
      </div>

      <div className='space-y-1.5 min-w-[200px]'>
        <label className='block text-xs font-medium text-muted-foreground' htmlFor='claims-group'>
          Group
        </label>
        <select
          id='claims-group'
          value={groupId ?? ''}
          onChange={(e) => updateParams({group: e.target.value || null})}
          className='flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring'
        >
          <option value=''>All groups</option>
          {options?.groups.map((g) => (
            <option key={g.groupId} value={g.groupId}>
              {g.groupName || g.groupId}
            </option>
          ))}
        </select>
      </div>

      <div className='space-y-1.5 min-w-[200px]'>
        <label className='block text-xs font-medium text-muted-foreground'>Status</label>
        <MultiSelectFilter
          placeholder='All statuses'
          options={statusOptions}
          selected={status}
          onChange={setStatus}
          className='w-full'
        />
      </div>

    </>
  );
}

function ActiveChip({label, onRemove}: {label: string; onRemove: () => void}) {
  return (
    <span className='inline-flex items-center gap-1 rounded-full bg-brand-50 border border-brand-200 text-brand-navy text-xs px-2.5 py-1 font-medium'>
      {label}
      <button
        type='button'
        onClick={onRemove}
        aria-label={`Remove filter ${label}`}
        className='ml-0.5 rounded-full hover:bg-brand-100 p-0.5'
      >
        <X className='h-3 w-3' />
      </button>
    </span>
  );
}
